import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  CheckCircle,
  Building2,
  User,
  Mail,
  Phone,
  CreditCard,
  Briefcase,
  Shield,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Public endpoints don't need /api prefix
const PUBLIC_API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicKYCFormPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    // Contact Info
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
    department: '',
    
    // Company Info
    company_name: '',
    company_address: '',
    company_country: '',
    vat_number: '',
    registration_number: '',
    
    // Accounting Details
    billing_email: '',
    accounts_payable_contact: '',
    payment_terms: '',
    bank_name: '',
    bank_account: '',
    bank_swift: '',
    
    // Business/KYC Info
    company_activity: '',
    number_of_employees: '',
    main_markets: '',
    annual_volume: '',
    
    // Consent
    gdpr_consent: false,
    marketing_consent: false
  });

  // Fetch existing data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API_URL}/public/kyc/${token}`);
        const data = response.data;
        
        setFormData(prev => ({
          ...prev,
          // Contact info
          first_name: data.contact?.first_name || '',
          last_name: data.contact?.last_name || '',
          email: data.contact?.email || '',
          phone: data.contact?.phone || '',
          position: data.contact?.position || '',
          department: data.contact?.department || '',
          
          // Company info
          company_name: data.company?.name || '',
          company_address: data.company?.address || '',
          company_country: data.company?.country || '',
          vat_number: data.company?.vat_number || '',
          registration_number: data.company?.registration_number || '',
          
          // Accounting
          billing_email: data.accounting?.billing_email || '',
          accounts_payable_contact: data.accounting?.accounts_payable_contact || '',
          payment_terms: data.accounting?.payment_terms || '',
          bank_name: data.accounting?.bank_name || '',
          bank_account: data.accounting?.bank_account || '',
          bank_swift: data.accounting?.bank_swift || '',
          
          // Business
          company_activity: data.business?.company_activity || '',
          number_of_employees: data.business?.number_of_employees || '',
          main_markets: data.business?.main_markets || '',
          annual_volume: data.business?.annual_volume || ''
        }));
      } catch (err) {
        console.error('Error fetching KYC data:', err);
        setError('Invalid or expired link. Please contact support.');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchData();
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.gdpr_consent) {
      toast.error('Please accept the data processing consent to continue');
      return;
    }

    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/public/kyc/${token}`, formData);
      setSubmitted(true);
      toast.success('Your information has been updated successfully!');
    } catch (err) {
      console.error('Error submitting KYC form:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading your information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Link Expired or Invalid</h2>
            <p className="mt-2 text-gray-600">{error}</p>
            <p className="mt-4 text-sm text-gray-500">
              Contact: <a href="mailto:info@europacific.com" className="text-blue-600">info@europacific.com</a>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Thank You!</h2>
            <p className="mt-2 text-gray-600">
              Your information has been updated successfully.
            </p>
            <p className="mt-4 text-sm text-gray-500">
              You may close this window.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/europacific-logo.jpg" 
            alt="Europacific" 
            className="h-12 mx-auto mb-4"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <h1 className="text-2xl font-bold text-gray-900">
            Contact Information Update
          </h1>
          <p className="mt-2 text-gray-600">
            Please review and update your contact and company information below.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section 1: Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-5 w-5 text-blue-600" />
                Contact Information
              </CardTitle>
              <CardDescription>
                Your personal contact details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    required
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    required
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      required
                      className="pl-9"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="phone"
                      className="pl-9"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="position">Position / Job Title</Label>
                  <Input
                    id="position"
                    value={formData.position}
                    onChange={(e) => handleChange('position', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Input
                    id="department"
                    value={formData.department}
                    onChange={(e) => handleChange('department', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 2: Company Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="h-5 w-5 text-blue-600" />
                Company Information
              </CardTitle>
              <CardDescription>
                Your organization details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  required
                  value={formData.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_address">Address</Label>
                <Input
                  id="company_address"
                  value={formData.company_address}
                  onChange={(e) => handleChange('company_address', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_country">Country</Label>
                  <Input
                    id="company_country"
                    value={formData.company_country}
                    onChange={(e) => handleChange('company_country', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vat_number">VAT Number</Label>
                  <Input
                    id="vat_number"
                    value={formData.vat_number}
                    onChange={(e) => handleChange('vat_number', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="registration_number">Registration Number</Label>
                  <Input
                    id="registration_number"
                    value={formData.registration_number}
                    onChange={(e) => handleChange('registration_number', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 3: Accounting Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="h-5 w-5 text-blue-600" />
                Accounting Details
              </CardTitle>
              <CardDescription>
                Billing and payment information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="billing_email">Billing Email</Label>
                  <Input
                    id="billing_email"
                    type="email"
                    value={formData.billing_email}
                    onChange={(e) => handleChange('billing_email', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accounts_payable_contact">Accounts Payable Contact</Label>
                  <Input
                    id="accounts_payable_contact"
                    value={formData.accounts_payable_contact}
                    onChange={(e) => handleChange('accounts_payable_contact', e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_terms">Payment Terms</Label>
                <Select
                  value={formData.payment_terms}
                  onValueChange={(value) => handleChange('payment_terms', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select payment terms..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prepaid">Prepaid</SelectItem>
                    <SelectItem value="net15">Net 15 days</SelectItem>
                    <SelectItem value="net30">Net 30 days</SelectItem>
                    <SelectItem value="net45">Net 45 days</SelectItem>
                    <SelectItem value="net60">Net 60 days</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input
                    id="bank_name"
                    value={formData.bank_name}
                    onChange={(e) => handleChange('bank_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_account">Account Number / IBAN</Label>
                  <Input
                    id="bank_account"
                    value={formData.bank_account}
                    onChange={(e) => handleChange('bank_account', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_swift">SWIFT/BIC Code</Label>
                  <Input
                    id="bank_swift"
                    value={formData.bank_swift}
                    onChange={(e) => handleChange('bank_swift', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 4: Business/KYC Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Briefcase className="h-5 w-5 text-blue-600" />
                Business Information
              </CardTitle>
              <CardDescription>
                Help us understand your business better
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company_activity">Main Business Activity</Label>
                <Input
                  id="company_activity"
                  placeholder="e.g., Manufacturing, Trading, Logistics..."
                  value={formData.company_activity}
                  onChange={(e) => handleChange('company_activity', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="number_of_employees">Number of Employees</Label>
                  <Select
                    value={formData.number_of_employees}
                    onValueChange={(value) => handleChange('number_of_employees', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1-10</SelectItem>
                      <SelectItem value="11-50">11-50</SelectItem>
                      <SelectItem value="51-200">51-200</SelectItem>
                      <SelectItem value="201-500">201-500</SelectItem>
                      <SelectItem value="500+">500+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="main_markets">Main Markets</Label>
                  <Input
                    id="main_markets"
                    placeholder="e.g., EU, Asia, Americas..."
                    value={formData.main_markets}
                    onChange={(e) => handleChange('main_markets', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="annual_volume">Annual Shipment Volume</Label>
                  <Select
                    value={formData.annual_volume}
                    onValueChange={(value) => handleChange('annual_volume', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1-10 TEU</SelectItem>
                      <SelectItem value="11-50">11-50 TEU</SelectItem>
                      <SelectItem value="51-200">51-200 TEU</SelectItem>
                      <SelectItem value="201-500">201-500 TEU</SelectItem>
                      <SelectItem value="500+">500+ TEU</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* GDPR Consent */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Shield className="h-5 w-5 text-blue-600" />
                Data Processing Consent
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 rounded"
                  checked={formData.gdpr_consent}
                  onChange={(e) => handleChange('gdpr_consent', e.target.checked)}
                />
                <span className="text-sm text-gray-700">
                  I confirm the above information is correct and consent to its storage and processing
                  for business communication purposes in accordance with GDPR regulations. *
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 rounded"
                  checked={formData.marketing_consent}
                  onChange={(e) => handleChange('marketing_consent', e.target.checked)}
                />
                <span className="text-sm text-gray-700">
                  I would like to receive updates about services, industry news, and promotions.
                </span>
              </label>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-center">
            <Button
              type="submit"
              size="lg"
              disabled={submitting || !formData.gdpr_consent}
              className="w-full md:w-auto px-12"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Information'
              )}
            </Button>
          </div>

          {/* Footer */}
          <div className="text-center text-sm text-gray-500 pt-4 border-t">
            <p>
              Europacific d.o.o. | Ankaranska cesta 5, 6000 Koper, Slovenia
            </p>
            <p className="mt-1">
              <a href="mailto:info@europacific.com" className="text-blue-600 hover:underline">
                info@europacific.com
              </a>
              {' | '}
              <a href="https://europacific.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                europacific.com
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
